import { z } from 'zod';
import { TypeOfAPIError, TypeOfAPIResponse } from '@ninja/api-client';
import { APIFactoryOptions, factory } from '../../../config';

const pagingAndSortingSchema = z.object({
	paging: z.object({
		pageSize: z.number(),
		pageNumber: z.number(),
	}),
	sorting: z.object({
		columnName: z.string(),
		sortOrder: z.union([z.literal(1), z.literal(2)]),
	}),
});

const requestSchema = z
	.object({
		policyNumber: z.string().optional(),
		policyTypeId: z.number().optional(),
		icName: z.number().optional(),
		mobileNumber: z.string().optional(),
		sponsorEID: z.string().optional(),
		sponsorName: z.string().optional(),
		customerEmail: z.string().optional(),
		policyEffectiveDateFrom: z.string().optional(),
		policyEffectiveDateTo: z.string().optional(),
		policyExpiryDateFrom: z.string().optional(),
		policyExpiryDateTo: z.string().optional(),
		policyIssueDateFrom: z.string().optional(),
		policyIssueDateTo: z.string().optional(),
		planId: z.number().optional(),
		channelTypeId: z.number().optional(),
		policyStatusId: z.number().optional(),
		paymentMethodId: z.number().optional(),
		quoteReferenceNumber: z.string().optional(),
		recordIds: z.array(z.string()).optional(),
		quoteReferenceNumbers: z.array(z.string()).optional(),
		policyNumbers: z.array(z.string()).optional(),
		planEmirateId: z.number().optional(),
		insuranceCard: z.boolean().optional(),
		isDiscountApplied: z.boolean().optional(),
		insuranceCardStatusId: z.number().optional(),
	})
	.merge(pagingAndSortingSchema);

const responseRecordSchema = z.object({
	encryptedPolicyId: z.string(),
	policyNumber: z.string(),
	mobileNumber: z.string(),
	quoteReferenceNumber: z.string(),
	membersCount: z.number(),
	policyTypeId: z.number(),
	policyTypeName: z.string(),
	channelTypeId: z.number(),
	channelTypeName: z.string(),
	planName: z.string(),
	icName: z.string(),
	policyIssueDate: z.string(),
	policyEffectiveDate: z.string(),
	policyExpiryDate: z.string(),
	totalPremiumExclVat: z.number(),
	totalPremiumInclVal: z.number(),
	basmahFees: z.number(),
	basmahFeesVat: z.number(),
	icpFeesExclVAT: z.number(),
	vatAmountOnICPFees: z.number(),
	dohFines: z.number(),
	policyStatusId: z.number(),
	policyStatusName: z.string(),
	customerId: z.string(),
	sponsorName: z.string(),
	icAdditionalFeesExcludingVAT: z.number(),
	vatOnICAdditionalFees: z.number(),
	planType: z.string(),
	planEmirate: z.string(),
	bmiLoading: z.number().nullish(),
	vatOnBMILoading: z.number().nullish(),
	isEcardsAvailable: z.boolean(),
	cancellationDetails: z.string(),
	isDiscountApplied: z.boolean().nullish(),
	typeOfDiscount: z.string().nullish(),
	discountRate: z.string().nullish(),
	discountExcludingVAT: z.number().nullish(),
	vaTonDiscount: z.number().nullish(),
	discountIncludingVAT: z.number().nullish(),
	actualAmountPaidIncludingVAT: z.number().nullish(),
	vatPercentage: z.number().nullish(),
	vatAmount: z.number().nullish(),
});

const responseSchema = z.object({
	data: z.array(responseRecordSchema),
	totalItemsCount: z.number(),
});

const postPurchasedPoliciesReport = (options: APIFactoryOptions) =>
	factory(options).createAPIDefinition({
		url: '/report/searchPolicies',
		method: 'POST',
		responseSchema,
		requestSchema,
		transformResponse: (response) => ({
			data: response.data.report,
			totalItemsCount: response.data.paging.totalRecords,
		}),
	});

export type PostPurchasedPoliciesReportRequest = z.infer<typeof requestSchema>;

export type PostPurchasedPoliciesReportResponse = TypeOfAPIResponse<ReturnType<typeof postPurchasedPoliciesReport>>;

export type PostPurchasedPoliciesReportRecord = z.infer<typeof responseRecordSchema>;

export type PostPurchasedPoliciesReportError = TypeOfAPIError<ReturnType<typeof postPurchasedPoliciesReport>>;

export default postPurchasedPoliciesReport;
